import "./App.scss";
import { useEffect, useState, createContext, useReducer } from "react";
import Charts from "./tabs/Charts";
import Team from "./tabs/Scorecard";
import Commentary from "./tabs/Commentary";
import Match from "./tabs/Match";
import Score from "./tabs/Score";
import Cartoons from "./tabs/Cartoons";
import EcricStream from "./tabs/EcricStream";
import Stats from "./tabs/Stats";
import { FaExclamationCircle } from "react-icons/fa";
import Spinner from "./components/Spinner";
import { parse } from "query-string";
import KabaddiCartoons from "./tabs/KabaddiCartoons";
import KabaddiScore from "./tabs/KabaddiScore";
import KabaddiCommentary from "./tabs/KabaddiCommentary";
import Scoregrid from "./tabs/Scoregrid";
import TabSelector from "./components/TabSelector";
import CountdownClock from "./components/CountdownClock";

const languages = ["English", "Hindi", "Tamil", "Bengali", "Marathi", "Telugu"];

export const LanguageContext = createContext();

// const LIVE_UPDATE_DELAY = 0; // delay for live games including kabaddi

const initialState = {
  currentState: {},
  nextState: {},
};

const reducer = (state, action) => {
  function getUpdatedCommentary(commentaries) {
    const { inns1, inns2, inns3, inns4 } = commentaries

    let update = null
    let updateName = null
    if (inns1 && inns1.length > 0) {
      update = inns1
      updateName = 'inns1'
    } else if (inns2 && inns2.length > 0) {
      update = inns2
      updateName = 'inns2'
    } else if (inns3 && inns3.length > 0) {
      update = inns3
      updateName = 'inns3'
    } else if (inns4 && inns4.length > 0) {
      update = inns4
      updateName = 'inns4'
    }

    const newCommentaryItem = update[0]
    let newInningsArray = null
    if (state.nextState.commentary.commentaries[updateName] === null) {
      newInningsArray = [newCommentaryItem]
    } else {
      newInningsArray = [...state.nextState.commentary.commentaries[updateName], newCommentaryItem]
    }

    return {
      ...state,
      nextState: {
        ...state.nextState,
        commentary: {
          ...state.nextState.commentary,
          commentaries: {
            ...state.nextState.commentary.commentaries,
            [updateName]: newInningsArray
          }
        },
      },
    };
  }
  switch (action.type) {
    case "addToNextState":
      // Replace commentary msg if commentary items > 1, otherwise append to existing commentary
      if (action.payload.commentary) {
        const { inns1, inns2, inns3, inns4 } = action.payload.commentary.commentaries
        if ((inns1 && inns1.length > 1) || 
          (inns2 && inns2.length > 1) ||
          (inns3 && inns3.length > 1) ||
          (inns4 && inns4.length > 1) || ! state.nextState.commentary) {
            return {
              ...state,
              nextState: {
                ...state.nextState,
                ...action.payload,
              },
            };
        } else {
          return getUpdatedCommentary(action.payload.commentary.commentaries)
        }
      }
      return {
        ...state,
        nextState: {
          ...state.nextState,
          ...action.payload,
        },
      };
    case "updateState":
      return {
        ...state,
        currentState: state.nextState,
      };
    case "clearState":
      return initialState;
    default:
      console.log("unknown action type");
      return null;
  }
};

function App() {
  // Find parent domains for ECRIC Twitch Player
  const findDomainAndSubdomainPattern = /^(?:https?:\/\/)?(?:[^@\n]+@)?([^:/\n?]+)/i;
  const origins = document.location.ancestorOrigins ?? []
  const originDomains = []
  for (const o of origins) {
    const regex = o.match(findDomainAndSubdomainPattern)
    if (regex && regex.length > 1) {
      originDomains.push(regex[1]) 
    }
  }

  // Find parent domain to send with feed connection request
  const findDomainPattern = /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/i;
  const url = document.location.ancestorOrigins ? document.location.ancestorOrigins[document.location.ancestorOrigins.length - 1] : "";
  let domain = null;
  if (url) {
    const regex = url.match(findDomainPattern)
    if (regex && regex.length > 1) {
      domain = regex[1];
    }
  }
  
  const [state, dispatch] = useReducer(reducer, initialState);
  const [channelValue, setChannelValue] = useState(1)
  const [isECN, setIsECN] = useState(false)
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedInnings, setSelectedInnings] = useState(null);

  const [language, setLanguage] = useState(languages[0]);

  let {
    id,
    backgroundColor,
    foregroundColor,
    frameBackgroundColor,
    iconColor,
    frameForegroundColor,
    layout,
    showHeader,
    fullscreenCartoon,
    cartoonOnly,
    parlay,
    teamFontSize,
    bip
  } = parse(window.location.search);

  if (backgroundColor === undefined) {
    backgroundColor = "#2c3e50";
  }
  if (foregroundColor === undefined) {
    foregroundColor = "#ffffff";
  }

  if (frameBackgroundColor === undefined) {
    frameBackgroundColor = "#d3e6f4";
  }

  if (frameForegroundColor === undefined) {
    frameForegroundColor = "#2c3e50";
  }

  if (layout === undefined) {
    layout = "single";
  }

  if (showHeader === "false") {
    showHeader = false
  } else {
    showHeader = true
  }

  if (fullscreenCartoon === "true") {
    fullscreenCartoon = true
  } else {
    fullscreenCartoon = false
  }

  if (cartoonOnly === "true") {
    cartoonOnly = true
  } else {
    cartoonOnly = false
  }

  if (teamFontSize === undefined) {
    teamFontSize = "1.3rem"
  }

  if (bip === "true") {
    bip = true
  } else {
    bip = false
  }

  if (parlay === "true") {
    parlay = true
  } else {
    parlay = false
  }

  let gameType = "LIVE";

  if (id) {
    if (id.includes("KABD")) {
      gameType = "KABD";
    } else if (id.includes("VRKBD")) {
      gameType = "VRKBD";
    } else if (id.includes("QUANTUM")) {
      gameType = "QUANTUM";
    } else if (id.includes("ECRIC")) {
      gameType = "ECRIC";
    }
  }

  // let delay = false;
  // if ((gameType === 'LIVE' || gameType === 'KABD') && ! bip) {
  //   delay = true;
  // }
  // if (gameType === 'QUANTUM' && domain && domain.includes('intelligentcricket')) {
  //   delay = true;
  // }

  function clearState() {
    dispatch({ type: "clearState" });
  }

  const [msgReady, setMsgReady] = useState(false);
  useEffect(() => {
    setMsgReady(false);

    const selectAGameTimer = setTimeout(() => {
      setMsgReady(true);
    }, 1000);

    return () => {
      clearTimeout(selectAGameTimer);
    };
  }, []);

  // useEffect(() => {
  //   function updateState() {
  //     dispatch({ type: "updateState" });
  //   }

  //   let updateDelay, updateStateInterval;

  //   if (delay) {
  //     updateDelay = LIVE_UPDATE_DELAY;
  //     updateStateInterval = setInterval(updateState, updateDelay);
  //   }

  //   return () => clearInterval(updateStateInterval);
  // }, [delay]);

  useEffect(() => {
    function setNextState(message) {
      dispatch({ type: "addToNextState", payload: message });
      dispatch({ type: "updateState" });
    }

    let socket = 0
    let reconnect_timer = 1000

    function connectToWidgetFeed() {

      if (!id) {
        console.log("no game id provided");
        return;
      }

      // Use intermediate widget subscription for the tracker. This may change in the future.
      let widgetId = "isbd";

      let connectionString = `${process.env.REACT_APP_WIDGET_FEED_URL}/${widgetId}/${id}/${domain}`

      if (bip) {
        connectionString += '/ic'
      } else {
        connectionString += '/external'
      }

      if (socket !== 0) {
        socket.close()
      }

      socket = new WebSocket(connectionString);

      socket.onmessage = function (event) {
        if (event.data === "{}") {
          return;
        }

        let msg = JSON.parse(event.data);

        // If the message contains status it is the heartbeat
        if ("status" in msg) {
          setNextState({ status: msg.status });
        } else {
          switch (msg.msg_type) {
            case "alerts":
              setNextState({ alerts: msg.alerts });
              break;
            case "match_update":
              if (msg.match_update.tier === 2){
                setIsECN(true)
              }
              setNextState({ match_update: msg.match_update });
              break;
            case "event":
              setNextState({ event: msg.event });
              break;
            case "exchange":
              setNextState({ exchange: msg.exchange });
              break;
            case "scoreboard":
              if (msg.scoreboard.channel && channelValue !== msg.scoreboard.channel) setChannelValue(msg.scoreboard.channel || 1)
              if (gameType === "KABD") {
                let chan = +msg.scoreboard.find((i) => i.name === "channel")?.value
                if (chan && channelValue !== chan) setChannelValue(chan || 1)
              } 
              setNextState({ scoreboard: msg.scoreboard });
              if (gameType === 'VRKBD') {
                const status = msg.scoreboard.find(i => i.name === 'Status')
                if (status && status.value === 'Event Closed') {
                  setTimeout(() => {
                    socket.close(1000, 'Match Complete')
                  }, 1000)
                }
              }
              break;
            case "lineups":
              setNextState({ lineups: msg.lineups });
              break;
            case "powerbar":
              setNextState({ powerbar: msg.powerbar });
              break;
            case "scorecard":
              setNextState({ scorecard: msg.scorecard });
              if (msg.scorecard.event_status && msg.scorecard.event_status.includes('Match Complete')) {
                setTimeout(() => {
                  socket.close(1000, 'Match Complete')
                }, 1000)
              }
              break;
            case "commentary":
              setNextState({ commentary: msg.commentary });
              break;
            case "scoregrid":
              setNextState({ scoregrid: msg.scoregrid });
              break;
            case "partnerships":
              setNextState({ partnerships: msg.partnerships });
              break;
            default:
              break;
          }
        }
      };

      socket.onopen = function(event) {
        reconnect_timer = 1000
      }

      socket.onclose = function(event) {
        if (event.code !== 1000) {
          setTimeout(() => {
            reconnect_timer = reconnect_timer * 2
            connectToWidgetFeed();
          }, reconnect_timer);
        }
      }
    }

    clearState();
    connectToWidgetFeed();

    return () => {
      socket.close();
    };
  }, [id, bip, gameType, /* delay, */ domain, channelValue]);

  // This fixes the position of the navigation on mobile to always be visible at the bottom of the screen
  useEffect(() => {
    function handleResize() {
      // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
      let vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }

    handleResize();

    // We listen to the resize event
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Tab selector buttons
  function handleScore() {
    setSelectedTab("score");
  }

  function handleScorecards() {
    setSelectedTab("scorecards");
  }

  function handleScoregrid() {
    setSelectedTab("scoregrid");
  }

  function handleMatch() {
    setSelectedTab("match");
  }

  function handleCommentary() {
    setSelectedTab("commentary");
  }

  function handleCartoons() {
    setSelectedTab("cartoons");
  }

  function handleCharts() {
    setSelectedTab("charts");
  }

  function handleStats() {
    setSelectedTab("stats");
  }

  function handleChangeLanguage(event) {
    setLanguage(event.target.value);
  }

  const { currentState } = state;

  const {
    event,
    exchange,
    match_update,
    commentary,
    scoreboard,
    lineups,
    powerbar,
    scorecard,
    scoregrid,
    partnerships,
    status,
  } = currentState;

  if (!event) {
    if (state.nextState.event) {
      return (
        <div
          className="not-ready"
          style={{ backgroundColor, color: foregroundColor }}
        >
          <Spinner />
        </div>
      );
    }
    return (
      <div
        className="not-ready"
        style={{ backgroundColor, color: foregroundColor }}
      >
        {msgReady && <div className="message">Please select a game</div>}
      </div>
    );
  }

  const { scoreboard: event_scoreboard, event_status } = event;

  function getValueFromScoreboard(name) {
    if (!scoreboard) {
      return null;
    }

    const found = scoreboard.find((i) => i.name === name);

    if (found) {
      return found.value;
    }

    return null;
  }

  function getValueFromEventScoreboard(name) {
    const found = event_scoreboard.find((i) => i.name === name);

    if (found) {
      return found.value;
    }

    return null;
  }

  if (!scoreboard) {
    if (gameType === "KABD" || gameType === "VRKBD") {
      if (event_status === "MATCH_SCHEDULED") {
        const home = getValueFromEventScoreboard("TeamA");
        const away = getValueFromEventScoreboard("TeamB");

        return (
          <div
            className="not-ready"
            style={{ backgroundColor, color: foregroundColor }}
          >
            <div>
              <span className="team">{home}</span> vs{" "}
              <span className="team">{away}</span>
            </div>
            {event_status === "MATCH_SCHEDULED" && (
              <CountdownClock event={event} match_update={match_update} />
            )}
          </div>
        );
      } else {
        // continue
      }
    } else {
      const home = getValueFromEventScoreboard("home");
      const away = getValueFromEventScoreboard("away");

      return (
        <div
          className="not-ready"
          style={{ backgroundColor, color: foregroundColor }}
        >
          <div>
            <span className="team">{home}</span> vs{" "}
            <span className="team">{away}</span>
          </div>
          {event_status === "MATCH_SCHEDULED" && (
            <CountdownClock event={event} match_update={match_update} />
          )}
        </div>
      );
    }
  }

  let innings1battingteam;
  let innings2battingteam;
  let innings3battingteam;
  let innings4battingteam;
  let clip;
  let currentbattingteam;
  let currentinnings;

  if (scoreboard) {
    innings1battingteam = scoreboard.innings1battingteam;
    innings2battingteam = scoreboard.innings2battingteam;
    innings3battingteam = scoreboard.innings3battingteam;
    innings4battingteam = scoreboard.innings4battingteam;
    clip = scoreboard.clip;
    currentbattingteam = scoreboard.currentbattingteam;
    currentinnings = scoreboard.currentinnings;
  }

  if (gameType === "KABD" || gameType === "VRKBD") {
    clip = getValueFromScoreboard("Clip");
  }

  let { matchtitle } = lineups || {};

  if (gameType === "KABD" || gameType === "VRKBD") {
    matchtitle = getValueFromScoreboard("Match.Title");
  }

  // Score tab is not included in double layout - switch to first available tab
  if (layout === "double") {
    if (selectedTab === "score") {
      if (gameType === "QUANTUM") {
        setSelectedTab("scoregrid");
      } else {
        setSelectedTab("commentary");
      }
    }
  }

  // Allow tab selection but default to cartoons for specific game types/modes
  let displayTab = selectedTab;
  if (displayTab === null) {
    if (
      gameType === "QUANTUM" ||
      gameType === "VRKBD" ||
      gameType === "ECRIC" ||
      cartoonOnly
    ) {
      setSelectedTab("cartoons");
    } else if (parlay) {
      setSelectedTab("commentary");
    } else {
      setSelectedTab("score");
    }
  }

  // Allow team selection but default to the team that is currently batting
  let displayInnings = selectedInnings;
  if (displayInnings === null) {
    displayInnings = Number(currentinnings);
  }

  let { teams } = lineups || {};

  // Fetch team colours or revert to defaults if team colours are missing
  if (teams) {
    if (teams[0].colour === "0" || teams[0].colour.length < 6) {
      teams[0].colour = "#1c8389";
    } else if (!teams[0].colour.includes("#")) {
      teams[0].colour = "#" + teams[0].colour;
    }

    if (teams[1].colour === "0" || teams[1].colour.length < 6) {
      teams[1].colour = "#cc4811";
    } else if (!teams[1].colour.includes("#")) {
      teams[1].colour = "#" + teams[1].colour;
    }

    if (teams[0].team_colour === "0" || teams[0].team_colour.length < 6) {
      teams[0].team_colour = "#fff";
    } else if (!teams[0].team_colour.includes("#")) {
      teams[0].team_colour = "#" + teams[0].team_colour;
    }

    if (teams[1].team_colour === "0" || teams[1].team_colour.length < 6) {
      teams[1].team_colour = "#fff";
    } else if (!teams[1].team_colour.includes("#")) {
      teams[1].team_colour = "#" + teams[1].team_colour;
    }
  } else {
    teams = [
      {
        colour: "#1c8389",
        team_colour: "#fff",
      },
      {
        colour: "#cc4811",
        team_colour: "#fff",
      },
    ];
  }

  let isHundred = false;
  if (match_update) {
    const { format } = match_update;

    if (format === 100) {
      isHundred = true;
    }
  }

  return (
    <div
      className="scoreboard"
      style={{ backgroundColor: backgroundColor, color: foregroundColor }}
    >
      {showHeader === true && gameType !== "QUANTUM" && cartoonOnly === false && (
        <div
          className="header"
          style={{
            color: frameForegroundColor,
            backgroundColor: frameBackgroundColor,
          }}
        >
          <div className="description">
            <div>{matchtitle && matchtitle.split(",")[0]}</div>
            <div style={{ fontSize: ".6rem", fontWeight: "normal" }}>
              {matchtitle &&
                matchtitle
                  .split(",")
                  .slice(1, matchtitle.split(",").length)
                  .join()}
            </div>
          </div>
          <select
            value={language}
            onChange={handleChangeLanguage}
            className="language-selector"
            style={{
              color: frameForegroundColor,
              background: frameBackgroundColor,
            }}
          >
            {languages.map((l) => (
              <option value={l} key={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
      )}

      {status === "DATA_ASSEMBLY_ISSUE" ? (
        <div className="data-assembly-issue">
          <FaExclamationCircle size={25} />
          We have lost connection to the game. Service will resume shortly.
        </div>
      ) : null}

      <LanguageContext.Provider value={language}>
        {layout === "double" ? (
          <div className="double">
            {gameType === "KABD" || gameType === "VRKBD" ? (
              <KabaddiScore
                event={event}
                match_update={match_update}
                teams={teams}
                teamFontSize={teamFontSize}
              />
            ) : (
              <Score
                event={event}
                teams={teams}
                scoreboard={scoreboard}
                powerbar={powerbar}
                scoregrid={scoregrid}
                isHundred={isHundred}
                teamFontSize={teamFontSize}
              />
            )}
          </div>
        ) : null}

        {layout === "double" && (
          <TabSelector
            gameType={gameType}
            selectedTab={displayTab}
            displayInnings={displayInnings}
            innings1battingteam={innings1battingteam}
            innings2battingteam={innings2battingteam}
            innings3battingteam={innings3battingteam}
            innings4battingteam={innings4battingteam}
            frameBackgroundColor={frameBackgroundColor}
            frameForegroundColor={frameForegroundColor}
            iconColor={iconColor}
            parlay={parlay}
            setSelectedInnings={setSelectedInnings}
            layout={layout}
            handleScore={handleScore}
            handleScoregrid={handleScoregrid}
            handleCommentary={handleCommentary}
            handleStats={handleStats}
            handleScorecards={handleScorecards}
            handleCharts={handleCharts}
            handleCartoons={handleCartoons}
            handleMatch={handleMatch}
          />
        )}

        <div className="selector-content">
          {selectedTab === "score" ? (
            gameType === "KABD" || gameType === "VRKBD" ? (
              <KabaddiScore
                event={event}
                match_update={match_update}
                teams={teams}
                scoreboard={scoreboard}
                teamFontSize={teamFontSize}
              />
            ) : (
              <Score
                event={event}
                exchange={exchange}
                match_update={match_update}
                teams={teams}
                scoreboard={scoreboard}
                powerbar={powerbar}
                scoregrid={scoregrid}
                language={language}
                teamFontSize={teamFontSize}
              />
            )
          ) : selectedTab === "scoregrid" ? (
            <Scoregrid
              scoregrid={scoregrid}
              teams={teams}
              currentBattingTeam={currentbattingteam}
              displayInnings={displayInnings}
            />
          ) : selectedTab === "scorecards" ? (
            <Team
              displayInnings={displayInnings}
              currentBattingTeam={currentbattingteam}
              eventScoreboard={event_scoreboard}
              scorecard={scorecard}
              teams={teams}
              partnerships={partnerships}
              isHundred={isHundred}
            />
          ) : selectedTab === "match" ? (
            <Match
              teams={teams}
              lineups={lineups}
              backgroundColor={backgroundColor}
              foregroundColor={foregroundColor}
              gameType={gameType}
            />
          ) : selectedTab === "commentary" ? (
            gameType === "KABD" || gameType === "VRKBD" ? (
              <KabaddiCommentary commentary={commentary} />
            ) : (
              <Commentary
                displayInnings={displayInnings}
                scoreboard={scoreboard}
                teams={teams}
                commentary={commentary}
                scoregrid={scoregrid}
                isHundred={isHundred}
              />
            )
          ) : selectedTab === "cartoons" ? (
            gameType === "KABD" || gameType === "VRKBD" ? (
              <KabaddiCartoons
                clip={clip}
                layout={layout}
                teams={teams}
                event={event}
                scorecard={scorecard}
                scoregrid={scoregrid}
                scoreboard={scoreboard}
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
                gameType={gameType}
                commentary={commentary}
                fullscreenCartoon={fullscreenCartoon}
                teamFontSize={teamFontSize}
                domain={domain}
              />
            ) : gameType === "ECRIC" ? (
              <EcricStream
                layout={layout}
                teams={teams}
                event={event}
                scorecard={scorecard}
                scoregrid={scoregrid}
                scoreboard={scoreboard}
                backgroundColor={backgroundColor}
                gameType={gameType}
                fullscreenCartoon={fullscreenCartoon}
                domains={originDomains}
                isECN={isECN}
                channel={channelValue}
              />
            ) : (
              <Cartoons
                clip={clip}
                layout={layout}
                teams={teams}
                event={event}
                scorecard={scorecard}
                scoregrid={scoregrid}
                scoreboard={scoreboard}
                backgroundColor={backgroundColor}
                gameType={gameType}
                isHundred={isHundred}
                fullscreenCartoon={fullscreenCartoon}
                cartoonOnly={cartoonOnly}
                teamFontSize={teamFontSize}
                domain={domain}
              />
            )
          ) : selectedTab === "charts" ? (
            <Charts
              teams={teams}
              scoregrid={scoregrid}
              event_scoreboard={event_scoreboard}
              backgroundColor={backgroundColor}
              foregroundColor={foregroundColor}
              scoreboard={scoreboard}
            />
          ) : selectedTab === "stats" ? (
            <Stats
              event_scoreboard={event_scoreboard}
              scoreboard={scoreboard}
            />
          ) : null}
        </div>

        {layout === "single" && ! cartoonOnly && (
          <TabSelector
            gameType={gameType}
            selectedTab={displayTab}
            displayInnings={displayInnings}
            innings1battingteam={innings1battingteam}
            innings2battingteam={innings2battingteam}
            innings3battingteam={innings3battingteam}
            innings4battingteam={innings4battingteam}
            frameBackgroundColor={frameBackgroundColor}
            frameForegroundColor={frameForegroundColor}
            iconColor={iconColor}
            parlay={parlay}
            setSelectedInnings={setSelectedInnings}
            layout={layout}
            handleScore={handleScore}
            handleScoregrid={handleScoregrid}
            handleCommentary={handleCommentary}
            handleStats={handleStats}
            handleScorecards={handleScorecards}
            handleCharts={handleCharts}
            handleCartoons={handleCartoons}
            handleMatch={handleMatch}
          />
        )}
      </LanguageContext.Provider>
    </div>
  );
}

export default App;
